import { alertController } from '@ionic/core/components';
import { html } from '../lib/html.js';
import { isValidCnpj, maskCnpj, maskPhone, normalizeCnpj, onlyDigits } from '../lib/masks.js';
import { applyApiFieldErrors, autoClearErrors, busy, confirm, setFieldError, toast, toastError, toastSuccess, valueOf, withLoading } from '../lib/ui.js';
import { me } from '../services/me.js';
import { space } from '../services/space.js';
import { WORKSHOP_STATUS, parseInviteToken, workshops } from '../services/workshops.js';
import { authorizations, fmtDateTime } from '../services/authorizations.js';
import { showcase } from '../services/showcase.js';
import { CATEGORY, CATEGORY_KEYS, categoryOf, vehicleId, vehicleName } from '../services/vehicles.js';
import { tabbarFooter } from '../lib/tabs.js';
import { openSpaceSheet, spaceButton } from '../lib/space-ui.js';
import { Page, go } from './base.js';
import { pill, sectionTitle } from './maintenance-ui.js';
import { catIcon } from './vehicle-hero.js';

const UF = /^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/;
const STATUS_TEXT = {
  PENDING: 'Recebemos o cadastro. Nossa equipe confere o CNPJ e libera a empresa; depois disso você já pode receber veículos e convidar sua equipe.',
  REJECTED: 'Corrija os dados da empresa e reenvie para análise.',
  BLOCKED: 'A empresa está bloqueada. Fale com o suporte do TicketCar.',
};
const dateFmt = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const fmtDate = (v) => (v ? dateFmt.format(new Date(v)) : '');
/** Linhas que a empresa atende (sem o campo = só linha leve). */
const linesOf = (w) => (w?.lines?.length ? w.lines : ['LIGHT']);
const linesText = (w) => linesOf(w).map((k) => (k === 'LIGHT' ? 'leve' : k === 'HEAVY' ? 'pesada' : 'agrícola')).join(', ').replace(/^./, (c) => `Linha ${c}`).replace(/, ([^,]*)$/, ' e $1');

/**
 * Espaço Empresa. A mesma classe monta três telas:
 *   /workshop          Painel   status da análise · Receber veículo · em atendimento · últimos 30 dias
 *   /oficina/empresa   Empresa  dados e linhas atendidas (editar/reenviar) · selo · perfil público · equipe
 *   /empresa/cadastrar         cadastrar empresa ou abrir convite (quem ainda não é de uma empresa)
 * Os veículos com acesso ficam na aba Veículos (workshop-vehicles.js).
 */
class WorkshopPage extends Page {
  /** @returns {'panel' | 'company' | 'join'} */
  get section() {
    return 'panel';
  }

  template() {
    if (this.section === 'join') {
      return html`
        <ion-header translucent>
          <ion-toolbar>
            <ion-buttons slot="start"><ion-back-button default-href="/profile" text="Voltar"></ion-back-button></ion-buttons>
            <ion-title>Sua empresa</ion-title>
          </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding tc-page" fullscreen>
          <div class="narrow body"></div>
        </ion-content>`;
    }
    return html`
      <ion-content class="ion-padding tc-page root-page has-tabbar" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow body"></div>
      </ion-content>
      ${tabbarFooter(this.section === 'company' ? 'company' : 'panel')}`;
  }

  setup() {
    this.$('ion-refresher')?.addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) return this.action(el.dataset.action, el.dataset.id, el);
      const g = e.target.closest('[data-go]');
      if (g && !g.hasAttribute('disabled')) go(g.dataset.go, g.dataset.dir || 'forward');
    });
  }

  onEnter() {
    this.$('tc-tabbar')?.refresh();
    this.lastInvite = null; // o link gerado só aparece enquanto a tela está aberta
    this.editing = false;
    this.load();
  }

  async load() {
    if (!this.state) this.$('.body').innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const [{ workshop, membership }] = await Promise.all([
        workshops.mine(),
        // tipos de serviço (troca de óleo, baterias...) para o formulário de cadastro/edição
        this.specs ? null : showcase.specialties().then((r) => (this.specs = r.items)).catch(() => (this.specs = [])),
      ]);
      const state = { workshop, membership, members: [], invitations: [], profile: null, stats: null, vehicles: null, verification: null };
      const manager = membership?.role === 'MANAGER';
      const approved = workshop?.status === 'APPROVED';
      const company = this.section === 'company';
      await Promise.all([
        company && manager && approved && Promise.all([workshops.members(), workshops.invitations()]).then(([m, i]) => {
          state.members = m.items;
          state.invitations = i.items.filter((x) => x.status === 'PENDING');
        }),
        // perfil, painel e veículos são extras: se falharem, a tela continua
        company && manager && workshop.status !== 'BLOCKED' && showcase.profile().then((r) => (state.profile = r.profile)).catch(() => {}),
        !company && manager && approved && showcase.stats(30).then((r) => (state.stats = r)).catch(() => {}),
        !company && approved && authorizations.workshopVehicles().then((r) => (state.vehicles = r.items)).catch(() => {}),
        company && approved && showcase.verification().then((r) => (state.verification = r.verification)).catch(() => {}),
      ]);
      this.state = state;
      this.render();
    } catch (err) {
      this.state = null;
      this.$('.body').innerHTML = html`
        <div class="center">
          <ion-icon name="cloud-offline-outline" class="empty-icon"></ion-icon>
          <p>${err.message}</p>
          <ion-button fill="outline" data-action="reload">Tentar de novo</ion-button>
        </div>`.value;
    }
  }

  render() {
    const { workshop: w, membership } = this.state;
    const body = this.$('.body');
    if (!w) {
      body.innerHTML = noWorkshop(this.specs, this.section !== 'join').value;
      this.bindForm({ create: true });
      return;
    }
    if (this.section === 'join') {
      // já faz parte de uma empresa: vai para o espaço Empresa
      space.set('business');
      return go('/workshop', 'root');
    }
    const manager = membership.role === 'MANAGER';
    if (this.section === 'company') {
      const showForm = manager && (w.status === 'REJECTED' || this.editing);
      body.innerHTML = html`
        ${titleRow('Empresa')}
        ${companyCard(w, manager, showForm)}
        ${showForm
          ? form(w.status === 'REJECTED' ? 'Reenviar para análise' : 'Salvar alterações', { create: false, cancel: w.status !== 'REJECTED', specs: this.specs, picked: (w.specialties ?? []).map((x) => x.key), lines: linesOf(w) })
          : ''}
        ${this.state.verification ? seal(this.state.verification, manager) : ''}
        ${this.state.profile ? publicProfile(this.state.profile) : ''}
        ${manager && w.status === 'APPROVED' ? team(this.state, this.lastInvite) : ''}
        ${!manager ? html`<p class="foot-hint">Você faz parte da equipe desta empresa como profissional. Para sair, fale com o responsável.</p>` : ''}`.value;
      if (showForm) this.bindForm({ create: false, workshop: w });
      return;
    }
    body.innerHTML = html`
      <div class="top-row">${spaceButton()}${w.status === 'APPROVED' ? '' : statusPill(w.status)}</div>
      <h1 class="page-title">Painel</h1>
      <p class="page-sub">${[linesText(w), [w.city, w.state].filter(Boolean).join('/')].filter(Boolean).join(' · ')}</p>
      ${statusNotice(w, manager)}
      ${w.status === 'APPROVED' ? receiveCard() : ''}
      ${w.status === 'APPROVED' ? attending(this.state.vehicles) : ''}
      ${this.state.stats ? dashboard(this.state.stats) : ''}
      ${!manager && w.status === 'APPROVED' ? html`<p class="foot-hint">Peça ao dono para gerar o código no app dele e toque em Receber veículo.</p>` : ''}`.value;
  }

  /** Liga máscaras, validação e envio do formulário de cadastro/edição. */
  bindForm({ create, workshop }) {
    const f = this.$('form.workshop-form');
    autoClearErrors(f);
    const input = (n) => f.querySelector(`ion-input[name="${n}"]`);
    const mask = (n, fn) => input(n)?.addEventListener('ionInput', (e) => (e.target.value = fn(e.target.value)));
    mask('cnpj', maskCnpj);
    mask('phone', maskPhone);
    mask('state', (v) => String(v ?? '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2));
    if (workshop) {
      input('name').value = workshop.name;
      input('legalName').value = workshop.legalName ?? '';
      input('phone').value = workshop.phone ? maskPhone(workshop.phone) : '';
      input('city').value = workshop.city ?? '';
      input('state').value = workshop.state ?? '';
    }
    f.querySelector('.spec-picker')?.addEventListener('click', (e) => {
      const chip = e.target.closest('button.chip');
      if (!chip) return;
      const on = !chip.classList.contains('on');
      chip.classList.toggle('on', on);
      chip.setAttribute('aria-pressed', String(on));
      f.querySelector('.spec-error').textContent = '';
    });
    bindLinePicker(f);
    f.addEventListener('submit', (e) => {
      e.preventDefault();
      this.save(f, create);
    });
    this.$('form.invite-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.openInvite();
    });
  }

  async save(f, create) {
    const val = (n) => valueOf(f, n);
    const data = { name: val('name'), legalName: val('legalName'), phone: val('phone'), city: val('city'), state: val('state').toUpperCase() };
    if (create) data.cnpj = normalizeCnpj(val('cnpj'));
    const err = (n, m) => setFieldError(f.querySelector(`ion-input[name="${n}"]`), m);

    let ok = true;
    if (data.name.length < 2) (ok = false), err('name', 'Informe o nome da empresa');
    if (create && !isValidCnpj(data.cnpj)) (ok = false), err('cnpj', 'CNPJ inválido');
    if (data.phone && onlyDigits(data.phone).length < 10) (ok = false), err('phone', 'Telefone inválido');
    if (data.state && !UF.test(data.state)) (ok = false), err('state', 'UF inválida');
    const lines = pickedLines(f);
    if (!lines.length) (ok = false), (f.querySelector('.line-error').textContent = 'Marque pelo menos uma linha');
    const picker = f.querySelector('.spec-picker');
    const specialties = picker ? [...picker.querySelectorAll('button.chip.on')].map((b) => b.dataset.id) : null;
    if (specialties && !specialties.length) (ok = false), (f.querySelector('.spec-error').textContent = 'Marque pelo menos um serviço');
    if (!ok) return;
    // Vazio não vai (a API ignora campos opcionais vazios).
    for (const k of Object.keys(data)) if (!data[k]) delete data[k];
    if (specialties) data.specialties = specialties;
    data.lines = lines;

    await busy(f.querySelector('.submit'), async () => {
      try {
        if (create) {
          await workshops.create(data);
          toastSuccess('Empresa cadastrada! Agora é só aguardar a análise.');
          space.set('business');
        } else {
          const wasRejected = this.state.workshop.status === 'REJECTED';
          await workshops.update(data);
          toastSuccess(wasRejected ? 'Reenviado para análise.' : 'Dados atualizados.');
        }
        this.editing = false;
        await me.load({ force: true }).catch(() => {});
        if (create) return go('/workshop', 'root');
        await this.load();
      } catch (e) {
        if (e.code === 'CNPJ_IN_USE') return err('cnpj', 'Este CNPJ já está cadastrado');
        if (e.code === 'ALREADY_MEMBER' || e.code === 'WORKSHOP_LOCKED') {
          toastError(e.message);
          return this.load();
        }
        if (!applyApiFieldErrors(f, e.details)) toastError(e.message);
      }
    });
  }

  openInvite() {
    const input = this.$('ion-input[name="invite"]');
    const token = parseInviteToken(input?.value);
    if (!token) return setFieldError(input, 'Cole o link ou o código do convite');
    go(`/convite/${token}`);
  }

  async action(name, id) {
    const s = this.state;
    switch (name) {
      case 'space':
        return openSpaceSheet();
      case 'reload':
        return this.load();
      case 'edit':
        this.editing = true;
        return this.render();
      case 'cancel-edit':
        this.editing = false;
        return this.render();
      case 'invite':
        return this.createInvite();
      case 'request-visit':
        return this.requestVisit();
      case 'copy':
        return copy(this.lastInvite.link);
      case 'share':
        return navigator.share?.({ title: 'Convite TicketCar', text: this.lastInvite.message }).catch(() => {});
      case 'revoke': {
        const sure = await confirm({ header: 'Cancelar convite?', message: 'O link deixa de funcionar.', confirmText: 'Cancelar convite', cancelText: 'Voltar', destructive: true });
        if (!sure) return;
        try {
          await workshops.revokeInvitation(id);
          if (this.lastInvite?.id === id) this.lastInvite = null;
          toast('Convite cancelado.');
        } catch (e) {
          toastError(e.message);
        }
        return this.load();
      }
      case 'remove': {
        const m = s.members.find((x) => x.id === id);
        const sure = await confirm({
          header: 'Remover da equipe?',
          message: `${m?.user?.name ?? 'Este profissional'} deixa de ter acesso à área da empresa.`,
          confirmText: 'Remover',
          destructive: true,
        });
        if (!sure) return;
        try {
          await workshops.removeMember(id);
          toast('Profissional removido da equipe.');
        } catch (e) {
          toastError(e.message);
        }
        return this.load();
      }
    }
  }

  async requestVisit() {
    const sure = await confirm({
      header: 'Pedir a visita do TicketCar?',
      message: 'Um representante vai combinar um horário para conferir a empresa: fachada, área de serviço, equipamentos e descarte. Aprovada, sua empresa ganha o selo Empresa Verificada por 12 meses.',
      confirmText: 'Pedir visita',
    });
    if (!sure) return;
    try {
      const r = await withLoading('Enviando...', () => showcase.requestVisit());
      this.state.verification = r.verification;
      toastSuccess('Pedido enviado. Nossa equipe vai entrar em contato.');
      this.render();
    } catch (e) {
      toastError(e.message);
    }
  }

  async createInvite() {
    const a = await alertController.create({
      header: 'Convidar profissional',
      message: 'Vamos gerar um link de uso único, válido por 7 dias. Envie para o profissional pelo WhatsApp.',
      inputs: [{ name: 'note', placeholder: 'Nome do profissional (opcional)', attributes: { maxlength: 120 } }],
      buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Gerar link', role: 'confirm' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'confirm') return;
    const note = String(data?.values?.note ?? '').trim();
    try {
      const r = await withLoading('Gerando link...', () => workshops.invite(note ? { note } : {}));
      const link = r.url ?? r.token;
      this.lastInvite = {
        id: r.invitation.id,
        note,
        link,
        expiresAt: r.invitation.expiresAt,
        message: `Olá${note ? `, ${note}` : ''}! Você foi convidado para a equipe da empresa ${this.state.workshop.name} no TicketCar. Crie sua conta e aceite o convite: ${link}`,
      };
      await this.load();
    } catch (e) {
      toastError(e.message);
    }
  }
}

class WorkshopCompanyPage extends WorkshopPage {
  get section() {
    return 'company';
  }
}

class WorkshopJoinPage extends WorkshopPage {
  get section() {
    return 'join';
  }
}

/* ------------------------------------------------------------ templates */

const monthYear = (d) => new Date(d).toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' });
const statusPill = (status) => pill(WORKSHOP_STATUS[status]?.color ?? 'medium', WORKSHOP_STATUS[status]?.label ?? status);

const titleRow = (title) => html`
  <div class="top-row">${spaceButton()}</div>
  <h1 class="page-title">${title}</h1>`;

/** Em análise / precisa de correção / bloqueada: um aviso no topo do Painel. */
function statusNotice(w, manager) {
  if (w.status === 'APPROVED') return '';
  const danger = w.status === 'REJECTED' || w.status === 'BLOCKED';
  return html`
    <div class="panel notice ${danger ? 'danger' : ''}" style="margin-top:16px">
      <span class="row-icon ${danger ? 'danger' : 'warning'}"><ion-icon name="${danger ? 'alert-circle-outline' : 'time-outline'}"></ion-icon></span>
      <div>
        <strong>${w.status === 'PENDING' ? 'Cadastro em análise' : w.status === 'REJECTED' ? 'Cadastro precisa de correção' : 'Empresa bloqueada'}</strong>
        <p>${STATUS_TEXT[w.status]}</p>
        ${(w.status === 'REJECTED' || w.status === 'BLOCKED') && w.statusReason ? html`<p class="reason"><b>Motivo:</b> ${w.statusReason}</p>` : ''}
        ${manager && w.status !== 'BLOCKED' ? html`<div class="notice-actions"><ion-button size="small" fill="outline" data-go="/oficina/empresa" data-dir="root">${w.status === 'REJECTED' ? 'Corrigir dados' : 'Ver dados'}</ion-button></div>` : ''}
      </div>
    </div>`;
}

const receiveCard = () => html`
  <button type="button" class="receive-card" data-go="/autorizacao">
    <span class="rc-ic"><ion-icon name="tc-qr" aria-hidden="true"></ion-icon></span>
    <span class="row-main"><strong>Receber veículo</strong><span>Leia o QR, digite os 6 números ou peça pela placa</span></span>
    <ion-icon class="chevron" name="chevron-forward" aria-hidden="true"></ion-icon>
  </button>`;

/** Até 3 veículos com acesso (os que vencem antes primeiro). A lista completa fica na aba Veículos. */
function attending(list) {
  const items = list ?? [];
  return html`
    ${sectionTitle('Em atendimento', items.length > 3 ? html`<button type="button" class="link-btn" data-go="/oficina/veiculos" data-dir="root">Ver ${items.length}</button>` : '')}
    ${items.length
      ? html`<div class="panel vlist">${items.slice(0, 3).map(vehicleRow)}</div>`
      : html`<div class="panel"><p class="panel-text">Nenhum veículo liberado agora. Toque em Receber veículo para ler o QR ou digitar os 6 números do dono, ou para pedir o acesso pela placa.</p></div>`}`;
}

/** Linha de veículo da empresa (também usada na aba Veículos). */
export function vehicleRow({ vehicle: v, expiresAt }, { showAction = true } = {}) {
  const cat = categoryOf(v);
  const id = vehicleId(v);
  const title = vehicleName(v) || id || cat.kind;
  return html`
    <button type="button" class="row tappable" data-go="/oficina/veiculos/${v.id}">
      ${catIcon(cat.key)}
      <span class="row-main">
        <span class="row-title">${title}</span>
        <span class="row-sub one-line">${[id === title ? '' : id, cat.key === 'LIGHT' ? '' : cat.label].filter(Boolean).join(' · ')}</span>
        <span class="row-meta">Acesso até ${fmtDateTime(expiresAt)}</span>
      </span>
      ${showAction ? html`<span class="mini-btn">Registrar</span>` : html`<ion-icon class="chevron" name="chevron-forward"></ion-icon>`}
    </button>`;
}

/** Últimos 30 dias: serviços, nota e cliques no perfil. */
function dashboard({ totals, servicesCount, rating, unansweredReviews = 0 }) {
  const kpi = (icon, label, value, extra = '') => html`<div class="kpi-tile"><span><ion-icon name="${icon}" aria-hidden="true"></ion-icon>${label}</span><b>${value}${extra}</b></div>`;
  const n = (x) => Number(x ?? 0).toLocaleString('pt-BR');
  return html`
    ${sectionTitle('Últimos 30 dias')}
    <div class="kpi-grid">
      ${kpi('eye-outline', 'Visitas ao perfil', n(totals.views))}
      ${kpi('logo-whatsapp', 'WhatsApp', n(totals.whatsapp))}
      ${kpi('call-outline', 'Ligações', n(totals.call))}
      ${kpi('navigate-outline', 'Rotas abertas', n(totals.route))}
    </div>
    <button type="button" class="panel row tappable rv-dash" data-go="/oficina/avaliacoes" style="margin-top:10px">
      <span class="row-icon"><ion-icon name="star-outline"></ion-icon></span>
      <div class="row-main">
        <span class="row-title">${rating ? `Nota ${rating.avg.toLocaleString('pt-BR', { minimumFractionDigits: 1 })} · ${rating.count} ${rating.count === 1 ? 'avaliação' : 'avaliações'}` : 'Avaliações'}</span>
        <span class="row-sub">${unansweredReviews ? `${unansweredReviews} sem resposta. Responder rápido aparece no seu perfil.` : rating ? 'Tudo respondido.' : 'O TicketCar pede a nota ao cliente no dia seguinte ao serviço.'}</span>
      </div>
      ${unansweredReviews ? html`<span class="pill warning">${unansweredReviews}</span>` : ''}
      <ion-icon class="chevron" name="chevron-forward"></ion-icon>
    </button>
    <p class="foot-hint">${servicesCount === 1 ? '1 serviço registrado no app aparece' : `${n(servicesCount)} serviços registrados no app aparecem`} no seu perfil.</p>`;
}

/** Dados da empresa + linhas atendidas, com o botão de editar (responsável). */
function companyCard(w, manager, editing) {
  const place = [w.city, w.state].filter(Boolean).join('/');
  return html`
    <div class="panel" style="margin-top:14px">
      <div class="row">
        <span class="avatar lg biz"><ion-icon name="storefront-outline" aria-hidden="true"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">${w.name}</span>
          <span class="row-sub mono">CNPJ ${w.cnpjFormatted}</span>
          ${place ? html`<span class="row-meta">${place}</span>` : ''}
        </div>
        ${statusPill(w.status)}
      </div>
      <div class="row">
        <div class="row-main">
          <span class="row-sub">Linhas atendidas</span>
          <span class="cat-icons" style="margin-top:6px">${linesOf(w).map((k) => html`<span class="cat-tag cat-${k}"><ion-icon name="${CATEGORY[k].icon}" aria-hidden="true"></ion-icon>${CATEGORY[k].label}</span>`)}</span>
        </div>
      </div>
      ${(w.status === 'REJECTED' || w.status === 'BLOCKED') && w.statusReason ? html`<p class="panel-text"><b>Motivo:</b> ${w.statusReason}</p>` : ''}
      ${manager && !editing && w.status !== 'BLOCKED' ? html`<div class="panel-foot"><ion-button expand="block" fill="outline" data-action="edit"><ion-icon slot="start" name="create-outline"></ion-icon>Editar dados e linhas</ion-button></div>` : ''}
    </div>`;
}

/** Selo Empresa Verificada: situação, validade e o pedido de visita (só o responsável pede). */
function seal(v, manager) {
  const active = v.status === 'VERIFIED' || v.status === 'EXPIRING';
  const canAsk = manager && !active && !v.requestedAt;
  const renew = manager && v.status === 'EXPIRING' && !v.requestedAt;
  const text = {
    VERIFIED: `Sua empresa aparece com o selo Empresa Verificada na busca, até ${monthYear(v.expiresAt)}.`,
    EXPIRING: `O selo vence em ${monthYear(v.expiresAt)}. Peça a visita de renovação para continuar como Empresa Verificada.`,
    EXPIRED: 'O selo venceu. Peça uma nova visita para voltar a aparecer como Empresa Verificada.',
    REVOKED: 'O selo foi retirado. Fale com o suporte do TicketCar.',
    NONE: 'Um representante do TicketCar confere a empresa no local. Aprovada, ela ganha o selo Empresa Verificada por 12 meses e aparece em destaque para os motoristas.',
  }[v.status];
  return html`
    ${sectionTitle('Selo Empresa Verificada')}
    <div class="panel">
      <div class="row">
        <span class="row-icon ${active ? 'success' : 'warning'}"><ion-icon name="shield-checkmark-outline"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">${active ? 'Empresa Verificada' : v.status === 'NONE' ? 'Ganhe o selo Empresa Verificada' : v.status === 'EXPIRED' ? 'Selo vencido' : 'Selo retirado'}</span>
          <span class="row-sub">${text}</span>
          ${v.requestedAt ? html`<span class="row-meta">Visita pedida em ${new Date(v.requestedAt).toLocaleDateString('pt-BR')}. Nossa equipe vai entrar em contato.</span>` : ''}
        </div>
      </div>
      ${canAsk || renew ? html`<div class="panel-foot"><ion-button expand="block" data-action="request-visit"><ion-icon slot="start" name="calendar-outline"></ion-icon>${renew ? 'Pedir visita de renovação' : 'Pedir a visita'}</ion-button></div>` : ''}
    </div>`;
}

/** Perfil público: % completo, se está na busca e o atalho para editar/ver. */
function publicProfile(p) {
  const c = p.completeness;
  const next = c.missing.find((m) => m.required) ?? c.missing[0];
  const text = p.visible
    ? 'Sua empresa aparece para os motoristas perto dela.'
    : c.listed
      ? 'Perfil pronto: aparece na busca assim que a empresa for aprovada.'
      : 'Complete endereço, mapa e telefone para aparecer para os motoristas.';
  return html`
    ${sectionTitle('Perfil público')}
    <div class="panel complete-card">
      <button type="button" class="complete-link tappable" data-go="/oficina/perfil">
        <span class="complete-head">
          <strong>Perfil ${c.percent}% completo</strong>
          <span class="link-btn">${next ? `Adicionar ${next.label.toLowerCase()}` : 'Editar'}</span>
        </span>
        <span class="bar" role="progressbar" aria-valuenow="${c.percent}" aria-valuemin="0" aria-valuemax="100"><i style="width:${c.percent}%"></i></span>
        <span class="row-sub">${text}</span>
      </button>
      <div class="panel-foot two">
        <ion-button fill="outline" data-go="/oficina/perfil"><ion-icon slot="start" name="create-outline"></ion-icon>Editar</ion-button>
        <ion-button fill="clear" data-go="/oficinas/${p.id}"><ion-icon slot="start" name="eye-outline"></ion-icon>Ver perfil</ion-button>
      </div>
    </div>`;
}

/** Linhas atendidas: Leve, Pesada e Agrícola (múltipla escolha, pelo menos uma). */
export function linePicker(selected = ['LIGHT']) {
  const desc = { LIGHT: 'Carros, motos e utilitários', HEAVY: 'Caminhões e ônibus', AGRO: 'Tratores, colheitadeiras e implementos' };
  return html`
    <div class="spec-field">
      <p class="spec-label">Linhas que a empresa atende</p>
      <div class="line-picker" role="group" aria-label="Linhas que a empresa atende">
        ${CATEGORY_KEYS.map((k) => {
          const on = selected.includes(k);
          return html`
            <button type="button" class="line-opt ${on ? 'on' : ''}" data-line="${k}" aria-pressed="${on ? 'true' : 'false'}">
              ${catIcon(k)}
              <span class="row-main"><b>${CATEGORY[k].label}</b><small>${desc[k]}</small></span>
              <span class="tick" aria-hidden="true"><ion-icon name="checkmark"></ion-icon></span>
            </button>`;
        })}
      </div>
      <p class="field-error line-error" role="alert"></p>
    </div>`;
}

export function bindLinePicker(root) {
  root.querySelector('.line-picker')?.addEventListener('click', (e) => {
    const b = e.target.closest('[data-line]');
    if (!b) return;
    const on = !b.classList.contains('on');
    b.classList.toggle('on', on);
    b.setAttribute('aria-pressed', String(on));
    const err = root.querySelector('.line-error');
    if (err) err.textContent = '';
  });
}

export const pickedLines = (root) => [...root.querySelectorAll('.line-opt.on')].map((b) => b.dataset.line);

/** Tipo de serviço: chips de múltipla escolha (o mesmo campo do perfil público). */
function servicePicker(specs = [], picked = []) {
  if (!specs.length) return '';
  return html`
    <div class="spec-field">
      <p class="spec-label">Serviços que você oferece</p>
      <div class="tags wrap spec-picker" role="group" aria-label="Serviços que você oferece">
        ${specs.map((s) => html`<button type="button" class="chip ${picked.includes(s.key) ? 'on' : ''}" aria-pressed="${picked.includes(s.key) ? 'true' : 'false'}" data-id="${s.key}">${s.name}</button>`)}
      </div>
      <p class="field-error spec-error" role="alert"></p>
    </div>`;
}

function form(submitLabel, { create, cancel, specs, picked, lines = ['LIGHT'] }) {
  return html`
    ${sectionTitle(create ? 'Dados da empresa' : 'Editar dados')}
    <form class="workshop-form" novalidate>
      <ion-list lines="none" class="form">
        <ion-input name="name" label="Nome da empresa" label-placement="floating" fill="outline" maxlength="120"></ion-input>
        ${create
          ? html`<ion-input name="cnpj" label="CNPJ" label-placement="floating" fill="outline" maxlength="18" autocapitalize="characters" helper-text="Não pode ser alterado depois"></ion-input>`
          : ''}
        ${linePicker(lines)}
        ${servicePicker(specs, picked)}
        <ion-input name="legalName" label="Razão social (opcional)" label-placement="floating" fill="outline" maxlength="160"></ion-input>
        <ion-input name="phone" label="Telefone da empresa (opcional)" label-placement="floating" fill="outline" inputmode="tel" maxlength="15"></ion-input>
        <div class="row">
          <ion-input name="city" label="Cidade" label-placement="floating" fill="outline" maxlength="80"></ion-input>
          <ion-input name="state" label="UF" label-placement="floating" fill="outline" maxlength="2" autocapitalize="characters" class="uf"></ion-input>
        </div>
      </ion-list>
      <ion-button type="submit" expand="block" class="submit">${submitLabel}</ion-button>
      ${cancel ? html`<ion-button expand="block" fill="clear" data-action="cancel-edit">Cancelar</ion-button>` : ''}
    </form>`;
}

function noWorkshop(specs, withTitle) {
  return html`
    ${withTitle ? html`<h1 class="page-title">Sua empresa</h1>` : ''}
    <div class="intro">
      <span class="cat-ic lg" style="margin:0 auto;background:var(--tc-biz-soft);color:var(--tc-biz-text)"><ion-icon name="storefront-outline"></ion-icon></span>
      <h2>Cadastre sua empresa</h2>
      <p class="hint">Oficina, concessionária, centro automotivo ou mecânica diesel e agrícola. Depois da análise do CNPJ, sua empresa recebe autorizações dos donos, registra os serviços e aparece na busca.</p>
    </div>
    ${form('Enviar para análise', { create: true, specs })}
    ${sectionTitle('Trabalha em uma empresa?')}
    <div class="panel">
      <p class="panel-text">Se a empresa já está no TicketCar, cole aqui o link que o responsável enviou.</p>
      <div class="panel-foot">
        <form class="invite-form" novalidate>
          <ion-input name="invite" label="Link ou código do convite" label-placement="floating" fill="outline" autocapitalize="off"></ion-input>
          <ion-button type="submit" expand="block" fill="outline" class="ion-margin-top">
            <ion-icon slot="start" name="mail-open-outline"></ion-icon>
            Abrir convite
          </ion-button>
        </form>
      </div>
    </div>`;
}

function team({ members, invitations }, lastInvite) {
  const wa = lastInvite ? `https://wa.me/?text=${encodeURIComponent(lastInvite.message)}` : '';
  return html`
    ${sectionTitle('Equipe', html`<span class="sec-count">${members.length}</span>`)}
    ${lastInvite
      ? html`
        <div class="panel share-box" style="margin-bottom:10px">
          <div class="panel-text">
            <strong>Link criado${lastInvite.note ? html` para ${lastInvite.note}` : ''}</strong>
            <p class="hint">Envie agora: por segurança, este link não aparece de novo. Se perder, gere outro. Vale até ${fmtDate(lastInvite.expiresAt)}.</p>
            <code class="link">${lastInvite.link}</code>
            <div class="share-actions">
              <ion-button href="${wa}" target="_blank" color="success"><ion-icon slot="start" name="logo-whatsapp"></ion-icon>WhatsApp</ion-button>
              ${typeof navigator.share === 'function' ? html`<ion-button fill="outline" data-action="share"><ion-icon slot="start" name="share-social-outline"></ion-icon>Compartilhar</ion-button>` : ''}
              <ion-button fill="outline" data-action="copy"><ion-icon slot="start" name="copy-outline"></ion-icon>Copiar</ion-button>
            </div>
          </div>
        </div>`
      : ''}
    <div class="panel">
      ${members.map(
        (m) => html`
          <div class="row">
            <span class="row-icon ${m.role === 'MANAGER' ? 'secondary' : ''}"><ion-icon name="person-outline"></ion-icon></span>
            <div class="row-main">
              <span class="row-title">${m.user?.name ?? '—'}</span>
              <span class="row-sub">${m.role === 'MANAGER' ? 'Responsável' : 'Profissional'}${m.user?.phone ? html` · ${maskPhone(m.user.phone)}` : ''}</span>
            </div>
            ${m.role === 'MECHANIC'
              ? html`<ion-button fill="clear" color="danger" data-action="remove" data-id="${m.id}" aria-label="Remover ${m.user?.name ?? ''}"><ion-icon slot="icon-only" name="trash-outline"></ion-icon></ion-button>`
              : ''}
          </div>`,
      )}
      ${invitations.map(
        (i) => html`
          <div class="row">
            <span class="row-icon warning"><ion-icon name="mail-outline"></ion-icon></span>
            <div class="row-main">
              <span class="row-title">${i.note || 'Convite sem nome'}</span>
              <span class="row-sub">Convite pendente · vale até ${fmtDate(i.expiresAt)}</span>
            </div>
            <ion-button fill="clear" color="medium" data-action="revoke" data-id="${i.id}">Cancelar</ion-button>
          </div>`,
      )}
      <div class="panel-foot"><ion-button expand="block" fill="outline" data-action="invite"><ion-icon slot="start" name="person-add-outline"></ion-icon>Convidar profissional</ion-button></div>
    </div>`;
}

async function copy(text) {
  try {
    await navigator.clipboard.writeText(text);
    toastSuccess('Link copiado.');
  } catch {
    toast('Não deu para copiar automaticamente. Selecione o link e copie.', 'warning', 4000);
  }
}

customElements.define('page-workshop', WorkshopPage);
customElements.define('page-workshop-company', WorkshopCompanyPage);
customElements.define('page-workshop-join', WorkshopJoinPage);

